import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, HelpCircle, Info, Lock, ShieldCheck } from '../../components/icons';
import { Symbol } from '../../components/symbol';
import { BrandLogo } from '../../components/brand-logo';
import { authService, type LoginResult } from '../../services/auth';
import { useAuthStore } from '../../store/auth-store';
import { extractErrorMessage } from '../../api/error-message';
import { TotpEnrolment } from '../2fa-setup/totp-enrolment';

// 2FA is mandatory. The password step answers with either a 2FA challenge
// (enrolled admin → 'totp' step) or, for an admin with no factor yet, an
// enrolment challenge (`enrolmentRequired`) → 'enrol' step, which sets up the
// authenticator inline and returns the first session. The API never issues a
// session on the password alone.
type Step =
  | { kind: 'credentials' }
  | { kind: 'totp'; challengeToken: string; expiresInSec: number }
  | { kind: 'enrol'; enrolmentToken: string };

type LoginResponse = LoginResult & { enrolmentRequired?: boolean };

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onCredentialsSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result: LoginResponse = await authService.login(email, password);
      if (result.twoFactorEnabled && result.challengeToken) {
        setStep({ kind: 'totp', challengeToken: result.challengeToken, expiresInSec: result.expiresInSec });
      } else if (result.enrolmentRequired && result.challengeToken) {
        setStep({ kind: 'enrol', enrolmentToken: result.challengeToken });
      } else if (result.tokens) {
        // Pre-mandatory-2FA API shape; kept so a web deploy that lands before
        // the API deploy still signs people in.
        const session = authService.toSession(result.tokens);
        setSession(session);
        navigate('/dashboard', { replace: true });
      } else {
        setError('Unexpected login response — try again.');
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onTotpSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (step.kind !== 'totp') return;
    setError(null);
    setBusy(true);
    try {
      const session = await authService.verify2fa(step.challengeToken, totp);
      setSession(session);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // One primary per view: the large, full-width accent button (v2 `.btn-lg`).
  const ctaClass = 'btn btn-primary btn-lg btn-block';
  const backLinkClass =
    'btn btn-quiet btn-block text-[14px] font-semibold text-ink-700 hover:text-admin-ink';

  return (
    <div className="grid min-h-screen grid-cols-1 bg-surface-card lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      {/* Brand moment — jamun panel with the torana flourish. */}
      <BrandPanel />

      {/* Form column — clean white. */}
      <div className="flex flex-col px-6 py-8 sm:px-10 lg:px-16">
        {/* Brand mark — shown here only when the brand panel is hidden; on wide
            screens the panel's large wordmark is the one logo on the page. */}
        <div className="flex items-center gap-2.5 lg:invisible">
          <BrandLogo height={28} />
          <div className="text-[11.5px] leading-none text-ink-500">Admin console</div>
        </div>

        {/* Centered auth card */}
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[400px]">
            {step.kind === 'credentials' && (
              <form onSubmit={onCredentialsSubmit} noValidate className="space-y-5">
                <header className="mb-7">
                  <h1 className="page-title">Welcome back</h1>
                  <p className="mt-1.5 text-[15px] text-ink-500">Sign in to your admin account</p>
                </header>

                <Field
                  id="email"
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="username"
                  placeholder="admin@utsaha.in"
                  required
                />
                <Field
                  id="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  required
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="grid h-9 w-9 place-items-center rounded-sm text-ink-500 transition-colors hover:bg-surface-2 hover:text-ink-900"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {error && <ErrorBanner message={error} />}

                <button type="submit" disabled={busy || !email || !password} className={ctaClass}>
                  <Lock size={18} />
                  {busy ? 'Signing in…' : 'Continue to 2FA'}
                </button>

                <InfoBanner icon={<Info size={18} className="mt-px shrink-0" />}>
                  Every admin account signs in with two-factor authentication (TOTP).
                </InfoBanner>
              </form>
            )}

            {step.kind === 'enrol' && (
              <div className="space-y-5">
                <header className="mb-7">
                  <h1 className="page-title">Set up two-factor auth</h1>
                  <p className="mt-1.5 text-[15px] text-ink-500">
                    Every admin account needs an authenticator app. Scan the QR with Google
                    Authenticator, Authy or 1Password, then enter the 6-digit code it shows.
                  </p>
                </header>

                <TotpEnrolment
                  bearer={step.enrolmentToken}
                  submitLabel="Enable 2FA and sign in"
                  onEnabled={(tokens) => {
                    if (tokens) {
                      setSession(authService.toSession(tokens));
                      navigate('/dashboard', { replace: true });
                    } else {
                      // Not expected for an enrolment token; fall back to a
                      // normal sign-in, which now asks for the code.
                      setStep({ kind: 'credentials' });
                    }
                  }}
                />

                <button
                  type="button"
                  onClick={() => {
                    setStep({ kind: 'credentials' });
                    setError(null);
                  }}
                  className={backLinkClass}
                >
                  <Symbol name="arrow_back" size={18} />
                  Use a different email
                </button>
              </div>
            )}

            {step.kind === 'totp' && (
              <form onSubmit={onTotpSubmit} noValidate className="space-y-5">
                <header className="mb-7">
                  <h1 className="page-title">Two-factor code</h1>
                  <p className="mt-1.5 text-[15px] text-ink-500">
                    Open your authenticator app and enter the 6-digit code.
                  </p>
                </header>

                <Field
                  id="totp"
                  label="6-digit code"
                  value={totp}
                  onChange={(v) => setTotp(v.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  pattern="\d{6}"
                  placeholder="123456"
                  autoFocus
                  required
                  code
                />

                {error && <ErrorBanner message={error} />}

                <button type="submit" disabled={busy || totp.length !== 6} className={ctaClass}>
                  <ShieldCheck size={18} />
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep({ kind: 'credentials' });
                    setTotp('');
                    setError(null);
                  }}
                  className={backLinkClass}
                >
                  <Symbol name="arrow_back" size={18} />
                  Use a different email
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[13px] text-ink-400">© 2025 Utsaaha Lifestyle Pvt. Ltd.</p>
      </div>
    </div>
  );
}

/**
 * The login's one brand moment (design system v2 hero): a jamun-800 panel with
 * the torana scallop — a haldi scallop row over a dashed kumkum line — along its
 * top edge, and the Gloock wordmark. Flat colour, no gradients or glows.
 */
function BrandPanel() {
  const areas: Array<{ icon: string; title: string; body: string }> = [
    { icon: 'storefront', title: 'Sellers and catalog', body: 'KYC, listings and moderation' },
    { icon: 'local_shipping', title: 'Orders and delivery', body: 'Every order, partner and handover' },
    { icon: 'account_balance_wallet', title: 'Payouts and refunds', body: 'Settlements, approvals and audit' },
  ];

  return (
    <div className="torana relative hidden flex-col justify-between overflow-hidden bg-shell px-12 pb-12 pt-16 text-[#FBEFF5] lg:flex xl:px-16">
      <span className="text-[13.5px] font-medium text-shell-muted">Admin console</span>

      <div className="max-w-lg">
        <BrandLogo height={84} className="mb-8 rounded-lg ring-4 ring-white/15" />
        <h2 className="font-display text-[clamp(48px,5.5vw,84px)] font-normal leading-[0.95] tracking-[-0.02em]">
          Utsaaha Lifestyle
        </h2>
        <p className="mt-5 max-w-[46ch] text-[17px] leading-relaxed text-[#E7C9D8]">
          Run the marketplace: sellers, orders, delivery partners, customers and platform
          configuration, all in one place.
        </p>

        <ul className="mt-10 grid gap-3">
          {areas.map((a) => (
            <li
              key={a.title}
              className="flex items-center gap-3.5 rounded-md border border-white/[0.12] bg-white/[0.06] px-4 py-3"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-white/10 text-haldi-300">
                <Symbol name={a.icon} size={20} />
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-semibold text-white">{a.title}</span>
                <span className="block text-[13px] text-shell-muted">{a.body}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Help affordance */}
      <button
        type="button"
        aria-label="Help"
        className="absolute bottom-8 right-8 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-shell-on transition-colors hover:bg-white/[0.18]"
      >
        <HelpCircle size={20} />
      </button>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  inputMode?: 'numeric' | 'text';
  pattern?: string;
  placeholder?: string;
  required?: boolean;
  trailing?: ReactNode;
  /** Render the value in the code face (one-time codes). */
  code?: boolean;
}

function Field(props: FieldProps) {
  return (
    <label htmlFor={props.id} className="block">
      <span className="field-label mb-1.5">{props.label}</span>
      <div className="relative">
        <input
          id={props.id}
          name={props.id}
          type={props.type ?? 'text'}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          autoComplete={props.autoComplete}
          autoFocus={props.autoFocus}
          inputMode={props.inputMode}
          pattern={props.pattern}
          placeholder={props.placeholder}
          required={props.required}
          className={
            'input !h-12 !text-[15px]' +
            (props.code ? ' font-mono !text-[18px] tracking-[0.3em]' : '') +
            (props.trailing ? ' pr-12' : '')
          }
        />
        {props.trailing && (
          <div className="absolute inset-y-0 right-1.5 flex items-center">{props.trailing}</div>
        )}
      </div>
    </label>
  );
}

function InfoBanner({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="alert alert-info !text-[13.5px]">
      {icon}
      <span>{children}</span>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="alert alert-danger !text-[13.5px]">
      <Symbol name="error" size={18} className="mt-px shrink-0" />
      <span>{message}</span>
    </div>
  );
}
